import { Field, Form, Formik } from 'formik';
import type { TFunction } from 'i18next';
import { HTTPError } from 'ky';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { FormComponentBaseProps } from '@tmh-website/components/blocks/Form';
import { cmsWebClient } from '@tmh-website/services/cms.client.service';
import { StaticFormPayload } from '@tmh-website/services/form.service';
import Yup from '@tmh-website/services/validation.service';
import { Breakpoint } from '@tmh-website/shared/const';
import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import Button from '@tmh-website/ui/Button';
import { type SelectOption, StyledFormElements } from '@tmh-website/ui/FormElements';
import { ErrorMessage, SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import Select from '@tmh-website/ui/Select';
import Textarea from '@tmh-website/ui/Textarea';

import * as StyledStaticForms from './StaticForms.styles';

const SHIPPING_FORM_CONCERN_TYPES: string[] = ['damaged', 'lost', 'other'];

export const ShippingFormValidation = Yup.object({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  company: Yup.string(),
  email: Yup.string().required(),
  phone: Yup.string().required(),
  orderNumber: Yup.string().required(),
  zipCode: Yup.string().required(),
  street: Yup.string().required(),
  city: Yup.string().required(),
  concern: Yup.string().required(),
  concernType: Yup.string().required().oneOf(SHIPPING_FORM_CONCERN_TYPES),
  gRecaptchaResponse: Yup.string().required(),
}).strict();

export type Values = Yup.InferType<typeof ShippingFormValidation>;

export const ShippingFormLabel: Record<keyof Values, string> = {
  firstName: 'global:label.firstName',
  lastName: 'global:label.lastName',
  company: 'global:label.company',
  email: 'global:label.email',
  phone: 'global:label.phone',
  orderNumber: 'blocks:staticForms.orderNumber',
  zipCode: 'global:label.zip',
  street: 'global:label.street',
  city: 'global:label.city',
  concern: 'blocks:staticForms.concernLabel',
  concernType: 'blocks:staticForms.concernTypeLabel',
  gRecaptchaResponse: '',
};

/**
 * Resolves the label translation corresponding to the passed-in value.
 *
 * @param optionValue - The value of the option which label we want to resolve.
 * @param t - The i18n translation function.
 *
 * @returns - Applicable label translation, or `optionValue`, if the label hasn't been found.
 */
const resolveConcernTypeOptionLabel = (optionValue: SelectOption['value'], t: TFunction): string => {
  const allOptions: SelectOption[] = t('blocks:staticForms.concernTypeOptions', {
    returnObjects: true,
  }) as SelectOption[];

  const targetOption: SelectOption | undefined = allOptions.find(option => option.value === optionValue);

  return targetOption?.label || `${optionValue}`;
};

const ShippingForm = ({
  formParameters,
  formElementThemeColor,
  buttonThemeColor,
  buttonHoverThemeColor,
  locale,
}: FormComponentBaseProps) => {
  const { t } = useTranslation();

  const {
    mutateAsync: submitMutation,
    isSuccess,
    isError,
  } = useMutation<void, HTTPError, Values>(values => {
    const fields: StaticFormPayload['fields'] = Object.keys(values)
      .filter(key => key !== 'gRecaptchaResponse')
      .map(name => ({
        label: t(ShippingFormLabel[name]),
        name,
        value: name === 'concernType' ? resolveConcernTypeOptionLabel(values[name], t) : values[name],
      }));

    const payload: StaticFormPayload = {
      fields,
      formParameters,
      gRecaptchaResponse: values.gRecaptchaResponse,
      locale: locale,
      pagePath: window.location.pathname,
    };

    return cmsWebClient.post('static-form/submit', { json: payload }).json();
  });

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        zipCode: '',
        street: '',
        city: '',
        concern: '',
        concernType: '',
        orderNumber: '',
        company: '',
        email: '',
        phone: '',
        gRecaptchaResponse: '',
      }}
      validationSchema={ShippingFormValidation}
      onSubmit={values => submitMutation(values)}>
      {({ isSubmitting, setFieldValue, errors, touched }) => (
        <Form data-cy='shippingForm'>
          <>
            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              {/* First name */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.firstName)}
                name='firstName'
                autoComplete='given-name'
                required
              />
              {/* END | First name */}

              {/* Last name */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.lastName)}
                name='lastName'
                autoComplete='family-name'
                required
              />
              {/* END | Last name */}
            </FormSection>

            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              {/* ZIP code */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.zipCode)}
                name='zipCode'
                autoComplete='zip-code'
                required
              />
              {/* END | ZIP code */}

              {/* Street */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.street)}
                name='street'
                autoComplete='street'
                required
              />
              {/* END | Street */}
            </FormSection>

            {/* City */}
            <FormSection numberOfColumns={1}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.city)}
                name='city'
                autoComplete='city'
                required
              />
            </FormSection>
            {/* END | City */}

            <FormSection numberOfColumns={2}>
              {/* Company */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.company)}
                name='company'
                autoComplete='organization'
              />
              {/* END | Company */}

              {/* Phone */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.phone)}
                name='phone'
                autoComplete='phone'
                type='tel'
                required
              />
              {/* END | Phone */}
            </FormSection>

            <FormSection numberOfColumns={1}>
              {/* E-mail */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.email)}
                name='email'
                autoComplete='email'
                type='email'
                required
              />
              {/* END | E-mail */}

              {/* Order number */}
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.orderNumber)}
                name='orderNumber'
                required
              />
              {/* END | Order number */}

              {/* Concern type */}
              <Field
                as={Select}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.concernType)}
                name='concernType'
                autoComplete='concernType'
                options={t('blocks:staticForms.concernTypeOptions', { returnObjects: true })}
                required
              />
              {/* END | Concern type */}

              {/* Message (concern description) */}
              <Field
                as={Textarea}
                themeColor={formElementThemeColor}
                label={t(ShippingFormLabel.concern)}
                name='concern'
                rows='4'
                autoComplete='concern'
                required
              />
              {/* END | Message (concern description) */}
            </FormSection>

            {/* Google reCAPTCHA */}
            <StyledStaticForms.CaptchaContainer>
              <ReCAPTCHA
                hl={getCaptchaHlBasedOnLocale(locale)}
                onChange={token => setFieldValue('gRecaptchaResponse', token)}
                sitekey={
                  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? process.env.NX_NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
                }
                size='normal'
              />

              {/* CAPTCHA error message */}
              {touched.gRecaptchaResponse && errors.gRecaptchaResponse && (
                <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
              )}
            </StyledStaticForms.CaptchaContainer>
            {/* END | Google reCAPTCHA */}

            {/* "Submit" button */}
            {!isSuccess && (
              <Button
                type='submit'
                themeColor={buttonThemeColor}
                hoverThemeColor={buttonHoverThemeColor}
                isLoading={isSubmitting}
                disabled={isSuccess || Object.keys(touched).length === 0 || Object.keys(errors).length > 0}
                minWidth>
                {t('global:send')}
              </Button>
            )}
            {/* END | "Submit" button */}

            {/* Success message/feedback */}
            {isSuccess && <SuccessMessage marginTop>{t('global:contact.thanksMessage')}</SuccessMessage>}

            {/* Error message/feedback */}
            {isError && <ErrorMessage marginTop>{t('global:errorUnknown')}</ErrorMessage>}
          </>
        </Form>
      )}
    </Formik>
  );
};
export default ShippingForm;
